import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useEffect, useState } from "react";
import {
  changeTemplateStatus,
  cloneBilltemplate,
  deleteTemplate,
  getTemplate,
  getTemplateList,
  getTemplateOptions,
  getTemplateSummary,
  saveBillTemplate,
  updateBillTemplate,
} from "./api";
import { BillTemplateSection } from "./types";
import {
  transformApiResponseToSections,
  transformSectionsToSavePayload,
} from "./utils";

export interface UseBillTemplateReturn {
  sections: BillTemplateSection[];
  setSections: (sections: BillTemplateSection[]) => void;
  templateName: string;
  setTemplateName: (name: string) => void;
  templateFormat: string;
  setTemplateFormat: (format: string) => void;
  isLoading: boolean;
  error: string | null;
  saveTemplate: () => Promise<void>;
  isSaving: boolean;
  editTemplate: (id: string) => Promise<void>;
  isUpdating: boolean;
  originalTemplateId: number | null;
  refetch: () => void;
  templateData: {
    htmlData: string;
    baseHtml: string;
    style?: string;
    components: Array<{
      id: number;
      componentName: string;
      htmlData: string;
      isActive: boolean;
      layoutSize?: string; // Made optional since some components might not have it
    }>;
    sequence: Record<string, number>;
  } | null;
}

export function useBillTemplate(id: string): UseBillTemplateReturn {
  const [sections, setSections] = useState<BillTemplateSection[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [templateFormat, setTemplateFormat] = useState("standard");
  const [originalTemplateId, setOriginalTemplateId] = useState<number | null>(
    null
  );
  const [templateData, setTemplateData] = useState<any>(null);

  const {
    data: apiResponse,
    isPending: isLoading,
    error: queryError,
    refetch,
  } = useSmartQuery(
    [...QueryKeyFactory.module.bx.billTemplate.template(), id],
    () => getTemplate(id),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const {
    mutateAsync: saveTemplateMutation,
    isPending: isSaving,
    error: saveError,
  } = useSmartMutation(async (payload: any) => {
    console.log("Saving template with payload:", payload);
    return saveBillTemplate(payload);
  });

  const {
    mutateAsync: updateTemplateMutation,
    isPending: isUpdating,
    error: updateError,
  } = useSmartMutation(
    async ({ id, payload }: { id: string; payload: any }) => {
      return updateBillTemplate(id, payload);
    }
  );

  useEffect(() => {
    if (apiResponse) {
      try {
        const transformedSections = transformApiResponseToSections(apiResponse);
        setSections(transformedSections);
        setTemplateName(apiResponse.result.templateName);
        setOriginalTemplateId(apiResponse.result.id);

        setTemplateData({
          htmlData: apiResponse.result.baseHtml,
          baseHtml: apiResponse.result.baseHtml,
          style: apiResponse.result.style,
          components: apiResponse.result.components.map((component) => ({
            id: component.id,
            componentName: component.baseHtml,
            htmlData: component.htmlData,
            isActive: component.isActive,
            layoutSize: component.layoutSize || "full",
          })),
          sequence: apiResponse.result.sequence,
        });
      } catch (err) {
        console.error("Error transforming template data:", err);
      }
    }
  }, [apiResponse]);

  const saveTemplate = async () => {
    if (!originalTemplateId) {
      throw new Error("No template ID available for saving");
    }

    try {
      const savePayload = transformSectionsToSavePayload(
        sections,
        originalTemplateId,
        getRemoteUtilityId(),
        templateName
      );
      await saveTemplateMutation(savePayload);
      refetch();
    } catch (err) {
      console.error("Error saving template:", err);
      throw err;
    }
  };

  const editTemplate = async (id: string) => {
    if (!originalTemplateId) {
      throw new Error("No template ID available for saving");
    }

    try {
      const savePayload = transformSectionsToSavePayload(
        sections,
        originalTemplateId,
        getRemoteUtilityId(),
        templateName
      );
      await updateTemplateMutation({ id, payload: savePayload });
      refetch();
    } catch (err) {
      console.error("Error saving template:", err);
      throw err;
    }
  };

  const error = queryError?.message || saveError?.message || null;

  return {
    sections,
    setSections,
    templateName,
    setTemplateName,
    templateFormat,
    setTemplateFormat,
    isLoading,
    error,
    saveTemplate,
    isSaving,
    editTemplate,
    isUpdating,
    originalTemplateId,
    refetch,
    templateData,
  };
}

export const useTemplateSummary = () => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.billTemplate.summary(), getRemoteUtilityId()],
    () => getTemplateSummary(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useTemplateList = (status?: string, searchData?: string) => {
  const mapStatusToBoolean = (status?: string): string | undefined => {
    switch (status) {
      case "active":
        return "true";
      case "inactive":
        return "false";
      case "all":
      default:
        return undefined;
    }
  };

  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billTemplate.templateList(),
      getRemoteUtilityId(),
      status,
      searchData,
    ],
    () => getTemplateList(mapStatusToBoolean(status), searchData),
    {
      staleTime: 3000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    }
  );
};

export const useCloneTemplate = () => {
  return useSmartMutation(async (id: string | number) => {
    return cloneBilltemplate(id);
  });
};

export const useChangeTemplateStatus = () => {
  return useSmartMutation(
    async ({ id, isActive }: { id: string | number; isActive: boolean }) => {
      return changeTemplateStatus(id, isActive);
    }
  );
};

export const useDeleteTemplate = () => {
  return useSmartMutation(async (id: string | number) => {
    return deleteTemplate(id);
  });
};

export const useTemplateOptions = () => {
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.billTemplate.templateOptions(),
      getRemoteUtilityId(),
    ],
    () => getTemplateOptions(),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
};
